import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api/axios';
import { usePermissions } from '../context/PermissionContext';
import ServiceActsPanel from './ServiceActsPanel';
import ServiceLinesEditor, { ServiceLine, CatalogService, ClientInstrument, serviceLinePayload, EQUIPMENT_CATEGORY_OPTIONS, warrantyLabel } from './ServiceLinesEditor';

interface Client {
  id: number;
  company_name?: string;
  contact_person?: string;
  phone?: string;
}

interface Product {
  id: number;
  name: string;
  code: string;
}

interface CaseImage {
  id: number;
  image_url: string;
  caption?: string;
  created_at: string;
}

interface Case {
  id: number;
  number: string;
  case_type: 'sales' | 'service';
  title: string;
  description?: string;
  resolution?: string;
  status: string;
  priority: string;
  client_id?: number;
  client?: Client;
  product_id?: number;
  product?: Product;
  assigned_to?: string;
  assigned_to_id?: number;
  assignee?: { id: number; full_name: string };
  image_url?: string;
  amount?: number;
  currency?: string;
  created_by?: number;
  creator?: { id: number; full_name: string };
  images: CaseImage[];
  service_items?: ServiceLine[];
  closed_at?: string;
  created_at: string;
}

interface AssignableUser {
  id: number;
  full_name: string;
  role: string;
  role_label?: string;
}

const statusConfig: Record<string, { label: string; color: string }> = {
  new:         { label: 'Yangi',        color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'Jarayonda',    color: 'bg-yellow-100 text-yellow-700' },
  waiting:     { label: 'Kutmoqda',     color: 'bg-orange-100 text-orange-700' },
  resolved:    { label: 'Hal qilindi',  color: 'bg-green-100 text-green-700' },
  closed:      { label: 'Yopildi',      color: 'bg-gray-100 text-gray-600' },
  cancelled:   { label: 'Bajarilmadi',  color: 'bg-red-100 text-red-700' },
};

const priorityConfig: Record<string, { label: string; color: string }> = {
  low:    { label: 'Past',        color: 'bg-gray-100 text-gray-600' },
  medium: { label: "O'rta",       color: 'bg-blue-100 text-blue-700' },
  high:   { label: 'Yuqori',      color: 'bg-orange-100 text-orange-700' },
  urgent: { label: 'Shoshilinch', color: 'bg-red-100 text-red-700' },
};

const currencySymbol: Record<string, string> = { usd: '$', eur: '€', uzs: "so'm" };
const formatMoney = (amount: number, currency?: string) =>
  `${amount.toLocaleString()} ${currency ? (currencySymbol[currency] || currency.toUpperCase()) : ''}`.trim();

// Mas'ul hodim nomini ko'rsatish: avval FK (assignee), bo'lmasa eski matn
const getAssigneeName = (c: { assignee?: { full_name: string }; assigned_to?: string }) =>
  c.assignee?.full_name || c.assigned_to || '—';

// Mas'ul hodim select'ida rollarni guruhlash tartibi
const ROLE_GROUP_ORDER = [
  'admin', 'director',
  'sales_head', 'sales_manager',
  'service_head', 'service_engineer',
  'warehouse_head', 'logistic_head', 'logist',
  'chief_accountant', 'accountant',
  'hr_manager', 'lawyer',
];

interface CaseListProps {
  caseType: 'sales' | 'service';
  title: string;
  icon: string;
  color: string;
}

const CaseList: React.FC<CaseListProps> = ({ caseType, title, icon, color }) => {
  const { canEdit } = usePermissions();
  const canFull = canEdit(caseType === 'sales' ? 'sales_cases' : 'service_cases');
  const canViewQuotations = canEdit(caseType === 'sales' ? 'sales_quotations' : 'service_quotations');
  const navigate = useNavigate();
  const location = useLocation();

  const emptyForm = {
    case_type: caseType,
    title: '',
    description: '',
    resolution: '',
    status: 'new',
    priority: 'medium',
    client_id: '',
    product_id: '',
    assigned_to: '',
    assigned_to_id: '',
    amount: '',
    currency: 'uzs',
  };

  const [cases, setCases] = useState<Case[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  // "Taalluqli xizmatlar" (faqat servis case): qatorlar + shu case mijozining uskunalari
  const [serviceLines, setServiceLines] = useState<ServiceLine[]>([]);
  const [caseInstruments, setCaseInstruments] = useState<ClientInstrument[] | null>(null);
  const [savedClientId, setSavedClientId] = useState<string>('');
  // Case'dan kelgan xizmat shartnomalari (dalolatnomalar uchun; ko'lam - backend)
  const [caseContracts, setCaseContracts] = useState<{ id: number; number: string; title: string }[]>([]);
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState<Case | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchData = async () => {
    try {
      const c = await api.get('/cases', { params: { case_type: caseType } });
      setCases(c.data);
    } catch (e) { console.error('Cases:', e); }
    try {
      const cl = await api.get('/clients');
      setClients(cl.data);
    } catch (e) { setClients([]); }
    try {
      const p = await api.get('/products');
      setProducts(p.data);
    } catch (e) { setProducts([]); }
    try {
      const u = await api.get('/users/assignable');
      setAssignableUsers(u.data);
    } catch (e) { setAssignableUsers([]); }
  };

  const refreshDetail = async (caseId: number) => {
    const res = await api.get(`/cases/${caseId}`);
    setShowDetail(res.data);
  };

  const { canView } = usePermissions();
  useEffect(() => {
    setCaseContracts([]);
    if (!showDetail || showDetail.case_type !== 'service' || !canView('service_acts')) return;
    api.get(`/service-acts/case-contracts/${showDetail.id}`).then(r => setCaseContracts(r.data)).catch(() => setCaseContracts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDetail?.id]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => { fetchData(); }, [caseType]);

  // Dashboard "So'nggi faoliyat" feed havolasi ?open=<id> bilan keladi - shu
  // case'ni to'g'ridan-to'g'ri detal oynasida ochadi. Ruxsat bo'lmasa (403) -
  // jim o'tkazib yuboriladi.
  useEffect(() => {
    const openId = new URLSearchParams(location.search).get('open');
    if (openId) {
      api.get(`/cases/${openId}`).then(res => setShowDetail(res.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = cases.filter(c => {
    const matchSearch = c.number.toLowerCase().includes(search.toLowerCase()) ||
      c.title.toLowerCase().includes(search.toLowerCase()) ||
      (c.assigned_to || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus ? c.status === filterStatus : true;
    const matchPriority = filterPriority ? c.priority === filterPriority : true;
    return matchSearch && matchStatus && matchPriority;
  });

  const openAdd = () => {
    setForm(emptyForm); setEditId(null); setError('');
    setServiceLines([]); setCaseInstruments(null); setSavedClientId('');
    setShowModal(true);
  };

  // Uskunalar ro'yxati faqat saqlangan case orqali (server FAQAT shu case
  // mijozining uskunalarini qaytaradi - service_module ruxsati shart emas).
  const loadCaseInstruments = async (caseId: number) => {
    try { const r = await api.get(`/cases/${caseId}/client-instruments`); setCaseInstruments(r.data); }
    catch { setCaseInstruments(null); }
  };

  const openEdit = (c: Case) => {
    setForm({
      case_type: c.case_type,
      title: c.title,
      description: c.description || '',
      resolution: c.resolution || '',
      status: c.status,
      priority: c.priority,
      client_id: c.client_id?.toString() || '',
      product_id: c.product_id?.toString() || '',
      assigned_to: c.assigned_to || '',
      assigned_to_id: c.assigned_to_id?.toString() || '',
      amount: c.amount?.toString() || '',
      currency: c.currency || 'uzs',
    });
    setServiceLines((c.service_items || []).map(l => ({ ...l })));
    setSavedClientId(c.client_id?.toString() || '');
    setCaseInstruments(null);
    if (caseType === 'service') loadCaseInstruments(c.id);
    setEditId(c.id); setError(''); setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!canFull) return;
    if (!form.title.trim()) { setError("Sarlavha kiritilishi kerak"); return; }
    if (caseType === 'service' && serviceLines.some(l => !l.name.trim())) { setError("Har bir xizmat qatorida nom bo'lishi kerak"); return; }
    setLoading(true); setError('');
    try {
      const payload = {
        ...form,
        client_id: form.client_id ? Number(form.client_id) : null,
        product_id: form.product_id ? Number(form.product_id) : null,
        assigned_to_id: form.assigned_to_id ? Number(form.assigned_to_id) : null,
        amount: form.amount ? Number(form.amount) : null,
        currency: form.amount || (caseType === 'service' && serviceLines.length) ? form.currency : null,
        ...(caseType === 'service' ? { service_items: serviceLines.map(serviceLinePayload) } : {}),
      };
      if (editId) await api.put(`/cases/${editId}`, payload);
      else await api.post('/cases', payload);
      setShowModal(false); fetchData();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Xatolik yuz berdi');
    } finally { setLoading(false); }
  };

  const handleStatusChange = async (id: number, status: string) => {
    if (!canFull) return;
    await api.put(`/cases/${id}`, { status });
    fetchData();
    if (showDetail) refreshDetail(id);
  };

  const handleDelete = async (id: number) => {
    if (!canFull) return;
    if (!window.confirm("O'chirishni tasdiqlaysizmi?")) return;
    await api.delete(`/cases/${id}`);
    setShowDetail(null); fetchData();
  };

  const handleImageUpload = async (caseId: number, files: FileList) => {
    if (!canFull) return;
    setUploadingImage(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const formData = new FormData();
        formData.append('file', files[i]);
        await api.post(`/cases/${caseId}/images`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      fetchData();
      refreshDetail(caseId);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Rasm yuklashda xato');
    } finally { setUploadingImage(false); }
  };

  const handleImageDelete = async (caseId: number, imageId: number) => {
    if (!canFull) return;
    if (!window.confirm("Rasmni o'chirishni tasdiqlaysizmi?")) return;
    await api.delete(`/cases/${caseId}/images/${imageId}`);
    fetchData();
    refreshDetail(caseId);
  };

  const handleExportPdf = async (caseId: number, number: string) => {
    try {
      const res = await api.get(`/cases/${caseId}/export/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${number}.pdf`; a.click();
    } catch (e) {
      alert('PDF yaratishda xato');
    }
  };

  const handleCreateQuotation = async (caseId: number, number: string) => {
    if (!window.confirm(`${number} asosida tijorat taklifi yaratilsinmi?`)) return;
    try {
      const res = await api.post(`/quotations/from-case/${caseId}`);
      const qNumber = res.data.number;
      const qType = res.data.quotation_type;
      const path = qType === 'service' ? '/service-quotations' : '/sales-quotations';
      if (window.confirm(`✅ Tijorat taklifi yaratildi: ${qNumber}\n\nHozir ko'rish uchun OK bosing.`)) {
        navigate(path);
      }
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Xato yuz berdi');
    }
  };

  const getClientName = (client?: Client) =>
    client?.company_name || client?.contact_person || '—';

  const openCount = cases.filter(c => !['closed', 'resolved', 'cancelled'].includes(c.status)).length;
  const urgentCount = cases.filter(c => c.priority === 'urgent' && !['closed', 'cancelled'].includes(c.status)).length;

  return (
    <div className="p-3 md:p-6">
      <div className="flex justify-between items-center mb-4 md:mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800">{icon} {title}</h2>
        <button onClick={() => canFull && openAdd()} disabled={!canFull}
          title={!canFull ? "Faqat ko'rish huquqi" : undefined}
          className={`px-3 md:px-4 py-2 rounded-lg text-sm text-white ${canFull ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`}>
          + <span className="hidden sm:inline">Yangi {title.toLowerCase()}</span>
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-4 md:mb-6">
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <p className="text-gray-500 text-xs md:text-sm">Jami</p>
          <p className="text-xl md:text-2xl font-bold text-gray-800">{cases.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <p className="text-gray-500 text-xs md:text-sm">Ochiq</p>
          <p className="text-xl md:text-2xl font-bold text-blue-600">{openCount}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <p className="text-gray-500 text-xs md:text-sm">Shoshilinch</p>
          <p className="text-xl md:text-2xl font-bold text-red-600">{urgentCount}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <p className="text-gray-500 text-xs md:text-sm">Yopilgan</p>
          <p className="text-xl md:text-2xl font-bold text-gray-500">
            {cases.filter(c => ['closed', 'cancelled'].includes(c.status)).length}
          </p>
        </div>
      </div>

      {urgentCount > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-red-700 text-sm">
          ⚠️ {urgentCount} ta shoshilinch {title.toLowerCase()} hal qilinishini kutmoqda!
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-3 mb-4">
        <input type="text" placeholder="🔍 Qidirish..." value={search}
          onChange={e => setSearch(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">Barcha holatlar</option>
          {Object.entries(statusConfig).map(([k, v]) =>
            <option key={k} value={k}>{v.label}</option>
          )}
        </select>
        <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">Barcha ustuvorliklar</option>
          {Object.entries(priorityConfig).map(([k, v]) =>
            <option key={k} value={k}>{v.label}</option>
          )}
        </select>
      </div>

      <div className="bg-white rounded-xl shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
            <tr>
              <th className="px-3 md:px-4 py-3 text-left">Raqam</th>
              <th className="px-3 md:px-4 py-3 text-left">Sarlavha</th>
              <th className="px-3 md:px-4 py-3 text-left hidden md:table-cell">Mijoz</th>
              <th className="px-3 md:px-4 py-3 text-left hidden md:table-cell">Mas'ul</th>
              <th className="px-3 md:px-4 py-3 text-left hidden md:table-cell">Summa</th>
              <th className="px-3 md:px-4 py-3 text-left hidden md:table-cell">Ustuvorlik</th>
              <th className="px-3 md:px-4 py-3 text-left hidden md:table-cell">Holat</th>
              <th className="px-3 md:px-4 py-3 text-left hidden md:table-cell">Sana</th>
              <th className="px-3 md:px-4 py-3 text-left hidden md:table-cell">Yaratuvchi</th>
              <th className="px-3 md:px-4 py-3 text-center">Amallar</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={10} className="text-center py-10 text-gray-400">{title} yo'q</td></tr>
            ) : filtered.map(c => (
              <tr key={c.id} className={`border-t hover:bg-gray-50 ${
                c.priority === 'urgent' && !['closed', 'cancelled'].includes(c.status) ? 'bg-red-50' : ''
              }`}>
                <td className="px-3 md:px-4 py-3 font-mono text-blue-600 font-medium text-xs md:text-sm">{c.number}</td>
                <td className="px-3 md:px-4 py-3 font-medium max-w-xs truncate">
                  {c.title}
                  {c.images.length > 0 && (
                    <span className="ml-2 text-xs text-gray-400">📷 {c.images.length}</span>
                  )}
                  {/* Mobil: yashiringan ustunlarning qisqacha ko'rinishi */}
                  <div className="md:hidden flex items-center gap-1 flex-wrap mt-1">
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${priorityConfig[c.priority]?.color}`}>
                      {priorityConfig[c.priority]?.label}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${statusConfig[c.status]?.color}`}>
                      {statusConfig[c.status]?.label}
                    </span>
                  </div>
                  <div className="md:hidden text-xs text-gray-400 mt-0.5 truncate">
                    {getClientName(c.client)} · {getAssigneeName(c)}
                    {c.amount != null && <> · {formatMoney(c.amount, c.currency)}</>}
                  </div>
                </td>
                <td className="px-3 md:px-4 py-3 text-gray-600 hidden md:table-cell">{getClientName(c.client)}</td>
                <td className="px-3 md:px-4 py-3 text-gray-600 hidden md:table-cell">{getAssigneeName(c)}</td>
                <td className="px-3 md:px-4 py-3 text-gray-600 hidden md:table-cell">
                  {c.amount != null ? formatMoney(c.amount, c.currency) : '—'}
                </td>
                <td className="px-3 md:px-4 py-3 hidden md:table-cell">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${priorityConfig[c.priority]?.color}`}>
                    {priorityConfig[c.priority]?.label}
                  </span>
                </td>
                <td className="px-3 md:px-4 py-3 hidden md:table-cell">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig[c.status]?.color}`}>
                    {statusConfig[c.status]?.label}
                  </span>
                </td>
                <td className="px-3 md:px-4 py-3 text-gray-500 text-xs hidden md:table-cell">
                  {new Date(c.created_at).toLocaleDateString('ru-RU')}
                </td>
                <td className="px-3 md:px-4 py-3 text-gray-600 text-xs hidden md:table-cell">
                  {c.creator?.full_name || '—'}
                </td>
                <td className="px-3 md:px-4 py-3">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={() => setShowDetail(c)} title="Ko'rish"
                      className="px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-xs">👁️</button>
                    <button onClick={() => handleExportPdf(c.id, c.number)} title="PDF yuklab olish"
                      className="px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs">📄</button>
                    {canViewQuotations && (
                      <button onClick={() => canFull && c.client_id && handleCreateQuotation(c.id, c.number)}
                        disabled={!canFull || !c.client_id}
                        title={!canFull ? "Faqat ko'rish huquqi" : !c.client_id ? "Avval mijoz tanlang" : "Tijorat taklifi yaratish"}
                        className={canFull && c.client_id ? "px-2 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 text-xs" : "px-2 py-1 rounded text-xs bg-gray-100 text-gray-400 cursor-not-allowed"}>📝</button>
                    )}
                    <button onClick={() => canFull && openEdit(c)} disabled={!canFull}
                      title={!canFull ? "Faqat ko'rish huquqi" : "Tahrirlash"}
                      className={canFull ? "px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-xs" : "px-2 py-1 rounded text-xs bg-gray-100 text-gray-400 cursor-not-allowed"}>✏️</button>
                    <button onClick={() => handleDelete(c.id)} disabled={!canFull}
                      title={!canFull ? "Faqat ko'rish huquqi" : "O'chirish"}
                      className={canFull ? "px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs" : "px-2 py-1 rounded text-xs bg-gray-100 text-gray-400 cursor-not-allowed"}>🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Yaratish/Tahrirlash Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-4 md:p-6 max-h-[95vh] overflow-y-auto">
            <h3 className="text-lg md:text-xl font-bold mb-4">
              {editId ? 'Tahrirlash' : `Yangi ${title.toLowerCase()}`}
            </h3>
            {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Sarlavha *</label>
                <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                  placeholder="Case sarlavhasi"
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mijoz</label>
                <select value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Tanlang —</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.company_name || c.contact_person}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bog'liq mahsulot</label>
                <select value={form.product_id} onChange={e => setForm({ ...form, product_id: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Tanlang —</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.name} ({p.code})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ustuvorlik</label>
                <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {Object.entries(priorityConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Holat</label>
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {Object.entries(statusConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Summa</label>
                <input type="number" min="0" step="0.01" value={form.amount}
                  onChange={e => setForm({ ...form, amount: e.target.value })}
                  placeholder="0.00"
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valyuta</label>
                <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}
                  disabled={!form.amount}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400">
                  <option value="uzs">UZS (so'm)</option>
                  <option value="usd">USD ($)</option>
                  <option value="eur">EUR (€)</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Mas'ul hodim</label>
                <select value={form.assigned_to_id}
                  onChange={e => setForm({ ...form, assigned_to_id: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Mas'ul hodimni tanlang —</option>
                  {Array.from(new Set(assignableUsers.map(u => u.role)))
                    .sort((a, b) => {
                      const ia = ROLE_GROUP_ORDER.indexOf(a);
                      const ib = ROLE_GROUP_ORDER.indexOf(b);
                      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
                    })
                    .map(role => {
                      const groupUsers = assignableUsers.filter(u => u.role === role);
                      const groupLabel = groupUsers[0]?.role_label || role;
                      return (
                        <optgroup key={role} label={groupLabel}>
                          {groupUsers.map(u => (
                            <option key={u.id} value={u.id}>{u.full_name}</option>
                          ))}
                        </optgroup>
                      );
                    })}
                </select>
                {form.assigned_to && !form.assigned_to_id && (
                  <p className="text-xs text-gray-400 mt-1">Eski qiymat: {form.assigned_to}</p>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Tavsif</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  rows={3} placeholder="Tavsif..."
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Yechim</label>
                <textarea value={form.resolution} onChange={e => setForm({ ...form, resolution: e.target.value })}
                  rows={3} placeholder="Qanday hal qilindi..."
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              {caseType === 'service' && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Taalluqli xizmatlar</label>
                  <ServiceLinesEditor
                    lines={serviceLines} onChange={setServiceLines}
                    catalog={(products as unknown as CatalogService[]).filter(p => p.product_type === 'service')}
                    instruments={editId && form.client_id && form.client_id === savedClientId ? caseInstruments : null}
                    instrumentsHint={!editId ? "Mijoz uskunasini ro'yxatdan tanlash uchun avval case'ni saqlang (hozircha erkin matn)."
                      : "Mijoz o'zgardi - uskunani ro'yxatdan tanlash uchun avval saqlang (hozircha erkin matn)."}
                    priceLabel="Taxminiy narx (QQS ichida)"
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50">Bekor qilish</button>
              <button onClick={handleSubmit} disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {loading ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-4 md:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <span className="font-mono text-blue-600 font-bold">{showDetail.number}</span>
                <h3 className="text-lg font-bold text-gray-800">{showDetail.title}</h3>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${priorityConfig[showDetail.priority]?.color}`}>
                {priorityConfig[showDetail.priority]?.label}
              </span>
            </div>

            {/* Holat o'zgartirish */}
            <div className="flex gap-2 mb-4 flex-wrap">
              {Object.entries(statusConfig).map(([key, val]) => (
                <button key={key}
                  onClick={() => handleStatusChange(showDetail.id, key)}
                  disabled={showDetail.status === key || !canFull}
                  title={!canFull ? "Faqat ko'rish huquqi" : undefined}
                  className={`px-3 py-1 rounded-lg text-xs font-medium border transition ${
                    showDetail.status === key || !canFull
                      ? 'opacity-50 cursor-not-allowed border-gray-200 ' + val.color
                      : 'hover:bg-gray-50 border-gray-300'
                  }`}>
                  {val.label}
                </button>
              ))}
            </div>

            {/* Ma'lumotlar */}
            <div className="space-y-2 text-sm mb-4">
              {showDetail.client && (
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-500">Mijoz</span>
                  <span className="font-medium">{getClientName(showDetail.client)}</span>
                </div>
              )}
              {showDetail.product && (
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-500">Mahsulot</span>
                  <span className="font-medium">{showDetail.product.name} ({showDetail.product.code})</span>
                </div>
              )}
              {showDetail.case_type === 'service' && (showDetail.service_items || []).length > 0 && (
                <div className="border-b pb-2">
                  <span className="text-gray-500">Taalluqli xizmatlar</span>
                  <ul className="mt-1 space-y-1 text-xs">
                    {(showDetail.service_items || []).map((l, i) => (
                      <li key={i} className="bg-gray-50 rounded px-2 py-1">
                        <span className="font-medium">{l.name}</span> — {l.quantity}
                        {l.price != null && <> × {Number(l.price).toLocaleString()}</>}
                        {l.equipment_name && <span className="text-gray-500"> · 🔬 {l.equipment_name}{l.equipment_serial ? ` (${l.equipment_serial})` : ''}</span>}
                        {l.equipment_category && <span className="text-gray-400"> · {EQUIPMENT_CATEGORY_OPTIONS[l.equipment_category]}</span>}
                        {warrantyLabel(l) && <span className="text-gray-500"> · kafolat: {warrantyLabel(l)}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {(showDetail.assignee || showDetail.assigned_to) && (
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-500">Mas'ul hodim</span>
                  <span className="font-medium">👤 {getAssigneeName(showDetail)}</span>
                </div>
              )}
              {showDetail.amount != null && (
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-500">Summa</span>
                  <span className="font-bold text-green-600">{formatMoney(showDetail.amount, showDetail.currency)}</span>
                </div>
              )}
              {showDetail.creator && (
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-500">Yaratuvchi</span>
                  <span className="font-medium">👤 {showDetail.creator.full_name}</span>
                </div>
              )}
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-500">Yaratilgan</span>
                <span>{new Date(showDetail.created_at).toLocaleDateString('ru-RU')}</span>
              </div>
              {showDetail.closed_at && (
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-500">Yopilgan</span>
                  <span>{new Date(showDetail.closed_at).toLocaleDateString('ru-RU')}</span>
                </div>
              )}
            </div>

            {/* Rasmlar */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <p className="text-xs font-semibold text-gray-500 uppercase">
                  Rasmlar ({showDetail.images.length})
                </p>
                {canFull && (
                  <label className={`px-3 py-1 bg-blue-50 text-blue-600 rounded-lg text-xs cursor-pointer hover:bg-blue-100 ${uploadingImage ? 'opacity-50' : ''}`}>
                    {uploadingImage ? '⏳ Yuklanmoqda...' : "+ Rasm qo'shish"}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      disabled={uploadingImage}
                      onChange={e => e.target.files && handleImageUpload(showDetail.id, e.target.files)}
                    />
                  </label>
                )}
              </div>

              {showDetail.images.length === 0 ? (
                canFull ? (
                  <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition">
                    <span className="text-xl mb-1">📷</span>
                    <span className="text-xs text-gray-500">Rasm yuklash (bir nechta tanlash mumkin)</span>
                    <input type="file" accept="image/*" multiple className="hidden"
                      onChange={e => e.target.files && handleImageUpload(showDetail.id, e.target.files)} />
                  </label>
                ) : (
                  <div className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-200 rounded-lg text-gray-400">
                    <span className="text-xl mb-1">📷</span>
                    <span className="text-xs">Rasmlar yo'q</span>
                  </div>
                )
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {showDetail.images.map(img => (
                    <div key={img.id} className="relative group">
                      <img
                        src={`http://127.0.0.1:8000${img.image_url}`}
                        alt=""
                        className="w-full h-24 object-cover rounded-lg border cursor-pointer hover:opacity-90"
                        onClick={() => setSelectedImage(`http://127.0.0.1:8000${img.image_url}`)}
                      />
                      {canFull && (
                        <button
                          onClick={() => handleImageDelete(showDetail.id, img.id)}
                          title="Rasmni o'chirish"
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 text-xs items-center justify-center hidden group-hover:flex">
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Tavsif */}
            {showDetail.description && (
              <div className="mb-3">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Tavsif</p>
                <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700">
                  {showDetail.description}
                </div>
              </div>
            )}

            {/* Yechim */}
            {showDetail.resolution && (
              <div className="mb-3">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Yechim</p>
                <div className="bg-green-50 rounded-lg p-3 text-sm text-gray-700">
                  {showDetail.resolution}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-4 flex-wrap">
              <button onClick={() => handleExportPdf(showDetail.id, showDetail.number)}
                title="PDF yuklab olish"
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm">
                📄 PDF
              </button>
              {canViewQuotations && (
                <button onClick={() => canFull && showDetail.client_id && handleCreateQuotation(showDetail.id, showDetail.number)}
                  disabled={!canFull || !showDetail.client_id}
                  title={!canFull ? "Faqat ko'rish huquqi" : !showDetail.client_id ? "Avval mijoz tanlang" : "Tijorat taklifi yaratish"}
                  className={`px-4 py-2 rounded-lg text-sm text-white ${canFull && showDetail.client_id ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-300 cursor-not-allowed'}`}>
                  📝 Quotation yaratish
                </button>
              )}
              {canViewQuotations && (
                <button
                  onClick={() => navigate(showDetail.case_type === 'service' ? '/service-quotations' : '/sales-quotations')}
                  className="px-4 py-2 rounded-lg text-sm bg-purple-100 text-purple-700 hover:bg-purple-200">
                  📋 Quotationlarni ko'rish
                </button>
              )}
              <button onClick={() => { if (canFull) { setShowDetail(null); openEdit(showDetail); } }}
                disabled={!canFull}
                title={!canFull ? "Faqat ko'rish huquqi" : "Tahrirlash"}
                className={`px-4 py-2 rounded-lg text-sm text-white ${canFull ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`}>
                ✏️ Tahrirlash
              </button>
              <button onClick={() => setShowDetail(null)}
                title="Yopish"
                className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Yopish</button>
            </div>
            {/* Case'dan kelgan xizmat shartnomalari bo'yicha dalolatnomalar (servis muhandisi - qoralama) */}
            {caseContracts.map(c => (
              <div key={c.id} className="mt-2">
                <p className="text-xs text-gray-500 mt-4">Shartnoma {c.number} — {c.title}</p>
                <ServiceActsPanel contractId={c.id} canFull={canEdit('service_acts')} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Rasm kattalashtirish — Detail modal ustida ochilishi mumkin, shuning uchun yuqoriroq z-index */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60]"
          onClick={() => setSelectedImage(null)}>
          <img src={selectedImage} alt="" className="max-w-4xl max-h-screen object-contain rounded-lg" />
          <button onClick={() => setSelectedImage(null)}
            title="Yopish"
            className="absolute top-4 right-4 text-white text-2xl bg-black bg-opacity-50 w-10 h-10 rounded-full flex items-center justify-center hover:bg-opacity-70">
            ✕
          </button>
        </div>
      )}
    </div>
  );
};

export default CaseList;
